"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, User } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12">
        <div className="h-12 w-3/4 bg-muted/30 rounded-md animate-pulse mb-6" />
        <div className="h-96 bg-muted/30 rounded-lg animate-pulse mb-8" />
        <div className="space-y-4">
          <div className="h-4 bg-muted/30 rounded animate-pulse" />
          <div className="h-4 bg-muted/30 rounded animate-pulse" />
          <div className="h-4 w-5/6 bg-muted/30 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl px-4 py-12">
      {}
      <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-6">
        {post.title}
      </h1>

      {}
      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
        {post.date && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4" />
            <time dateTime={post.date}>
              {new Date(post.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric"
              })}
            </time>
          </div>
        )}
        {post.author && (
          <div className="flex items-center gap-2">
            <User className="size-4" />
            <span>{post.author.name}</span>
          </div>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="mb-8 rounded-lg overflow-hidden shadow-medium">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {}
      <div
        className="prose prose-invert prose-lg max-w-none mb-12"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-12" />

      {}
      {post.author && (
        <div className="glass-effect p-6 mb-12">
          <div className="flex items-start gap-4">
            <Avatar className="size-16 border-2 border-accent">
              {post.author.avatar?.["96"] && (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-accent/20 text-foreground font-semibold">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                About {post.author.name}
              </h3>
              <p className="text-muted-foreground text-sm">
                Author and contributor at VAZE REALTY
              </p>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsSection() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-64 rounded-lg bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="card">
          {post.featuredImage && (
            <div className="overflow-hidden rounded-t-md">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-48 object-cover"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-lg font-display text-foreground line-clamp-2">
              {post.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {post.excerpt && (
              <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                {post.excerpt.replace(/<[^>]*>/g, "")}
              </p>
            )}
            {post.link && (
              <Link
                to={post.link}
                className="text-accent hover:text-accent-hover text-sm font-semibold transition-colors duration-[var(--duration-medium)]"
              >
                Read Article →
              </Link>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <section id="single-post-template-section" className="bg-background py-16">
      {}
      <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>

      {}
      <div className="mx-auto max-w-7xl px-4">
        <h2 className="text-3xl font-display font-bold text-foreground mb-8">
          Related Articles
        </h2>
        <PostsProvider postType="post" wp_query={{ per_page: 3, order: "desc", orderby: "date", exclude: [currentPostId] }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "_embedded", "featured_media", "_links.wp:featuredmedia"]}>
          <RelatedPostsSection />
        </PostsProvider>
      </div>
    </section>
  );
}